import { useEffect, useState, useCallback, useRef } from 'react';

import getCodefAccessToken from '~apis/bank/getCodefAccessToken';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';
import {
  CodefCertificateRaw,
  GetCertificationRequestPayload,
  GetCertificationResponsePayload,
} from '~types/codef';

import useCodefStore from '../store/codef';

const CODEF_UPDATE_ERROR = 'E010001';
const CODEF_INSTALL_ERROR = 'E010002';

export interface UseCodefResult {
  requestCheckLicense: (token: string) => Promise<void>;
  requestGetCertification: (drive: string) => Promise<CodefCertificateRaw[]>;
  requestExportCertificate: (
    payload: GetCertificationRequestPayload,
  ) => Promise<GetCertificationResponsePayload>;
  requestExternalDrive: () => Promise<string[]>;
  requestInitialization: () => Promise<unknown>;

  loading: boolean;
  isLicensed: boolean;
  isInitialized: boolean;
}

function useCodef(): UseCodefResult {
  const { codefState, setCodefState } = useCodefStore();
  const [loading, setLoading] = useState(false);
  const initializationSuccessTimeoutRef = useRef<number>();
  const initializationErrorTimeoutRef = useRef<number>();

  useEffect(() => {
    setLoading(true);
    getCodefAccessToken().then(({ Data }) => {
      setCodefState({ token: Data?.data?.accessToken });
    });

    return () => {
      window.clearTimeout(initializationSuccessTimeoutRef.current);
      window.clearTimeout(initializationErrorTimeoutRef.current);
    };
  }, [setCodefState]);

  const requestCheckLicense = useCallback((token?: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!token) {
        reject(new Error('Token is required.'));
      } else {
        window.codefcert.options.codefToken = token;
        window.codefcert.engineCheckLicense(() => {
          resolve();
        });
      }
    });
  }, []);

  useEffect(() => {
    if (!codefState.token) return;
    requestCheckLicense(codefState?.token).then(() => {
      setCodefState({ isLicensed: true });
    });
  }, [codefState.token, requestCheckLicense, setCodefState]);

  const requestGetCertification = useCallback(
    (drive: string): Promise<CodefCertificateRaw[]> => {
      return new Promise((resolve, reject) => {
        if (!codefState.isLicensed) {
          reject(new Error('Should be licensed.'));
          return;
        }

        window.codefcert.engineGetCertification(drive, (payload) => {
          resolve(Array.isArray(payload) ? payload : []);
        });
      });
    },
    [codefState.isLicensed],
  );

  const requestExportCertificate = useCallback(
    (payload: GetCertificationRequestPayload): Promise<GetCertificationResponsePayload> => {
      return new Promise((resolve, reject) => {
        if (!codefState.isLicensed) {
          reject(new Error('Should be licensed.'));
          return;
        }

        window.codefcert.engineGetExportCertificationB64(payload, (callbackPayload) => {
          if (!callbackPayload.SUCCESS) {
            reject(new Error('Error receiving certificate'));
            return;
          }
          resolve(callbackPayload);
        });
      });
    },
    [codefState.isLicensed],
  );

  const requestExternalDrive = useCallback((): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      if (!codefState.isLicensed) {
        reject(new Error('Should be licensed.'));
      } else {
        window.codefcert.engineGetExternalDrive((payload) => {
          if (!Array.isArray(payload)) {
            reject(new Error('Error receiving certificate'));
            return;
          }
          resolve(payload);
        });
      }
    });
  }, [codefState.isLicensed]);

  const requestInitialization = useCallback(() => {
    mixpanel.track(MIXPANEL_EVENT.CERT.VIEW_INSTALL_CODEF);

    const requestPromise = new Promise<void>((resolve, reject) => {
      window.codefcert.initialization((isInitialized, errorCode) => {
        clearTimeout(initializationErrorTimeoutRef.current);
        // NOTE - 연결을 위해 지연 시간을 둔다.
        initializationSuccessTimeoutRef.current = window.setTimeout(() => {
          if (isInitialized) {
            setCodefState({ isInitialized: true });
            resolve();
          } else {
            if (errorCode === CODEF_INSTALL_ERROR) {
              // TODO - Handle error checks with toast.
              reject(new Error('Initialization fail.'));
            } else if (errorCode === CODEF_UPDATE_ERROR) {
              // TODO - Handle error checks with toast.
              reject(new Error('Require update.'));
            }

            reject(new Error(errorCode));
          }

          setLoading(false);
        }, 2000);
      });
    });
    return requestPromise;
  }, [codefState.isLicensed, setCodefState]);

  return {
    requestCheckLicense,
    requestGetCertification,
    requestExportCertificate,
    requestExternalDrive,
    requestInitialization,

    loading,
    isLicensed: Boolean(codefState.isLicensed),
    isInitialized: Boolean(codefState.isInitialized),
  };
}

export default useCodef;
